/**
 * Ties a chat failure to the Sentry session replay of the browser that saw it
 * (AMO-57).
 *
 * Each `thread.turn.start` carries {@link currentClientReplay}: the replay's
 * id, or why there is none. The server puts it on the failure report, and the
 * #war-room alert and Linear incident link to it.
 *
 * Most consented sessions only buffer the last minute (`startBuffering`), and
 * Sentry uploads a buffer by itself only on a browser error. A failed turn is
 * a server failure, so when the open thread shows one, {@link flushReplayForFailure}
 * uploads the buffer under the same id. Masking is unchanged (./sentry.ts).
 */
import type { ClientReplay } from "@amoreg/contracts";
import { getAnalyticsConsent } from "@repo/shared/analyticsConsent";
import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";

/** The parts of Sentry this module reads, so the rules can be tested without it. */
export interface ReplayProbe {
  readonly sentryEnabled: boolean;
  readonly consentAccepted: boolean;
  readonly replay:
    | { getReplayId(): string | undefined; flush(): Promise<void> }
    | undefined;
}

const SENTRY_REPLAY_ID = /^[0-9a-f]{32}$/;

const liveProbe = (): ReplayProbe => ({
  sentryEnabled: Sentry.isEnabled(),
  consentAccepted: getAnalyticsConsent() === "accepted",
  replay: Sentry.getReplay(),
});

/** The replay to send with a turn: its id, or why there is none. */
export function clientReplayOf(probe: ReplayProbe): ClientReplay {
  if (!probe.sentryEnabled) return { absent: "sentry_off" };
  if (!probe.consentAccepted) return { absent: "no_consent" };
  const replayId = probe.replay?.getReplayId();
  return replayId !== undefined && SENTRY_REPLAY_ID.test(replayId)
    ? { replayId }
    : { absent: "not_recording" };
}

export const currentClientReplay = (): ClientReplay => clientReplayOf(liveProbe());

/**
 * Upload the replay's buffer, so the minute before the failure is kept. Only
 * a replay already recording with consent: Sentry's `flush()` starts a new
 * recording when none is running, which must never happen without consent.
 */
export async function flushReplayForFailure(probe: ReplayProbe = liveProbe()): Promise<boolean> {
  const replay = probe.replay;
  if (replay === undefined || "absent" in clientReplayOf(probe)) return false;
  try {
    await replay.flush();
    return true;
  } catch {
    // A replay that fails to upload must not affect the chat.
    return false;
  }
}

export interface ShownFailure {
  readonly threadId: string | null;
  readonly error: string | null;
}

/** Threads this tab sent a turn on whose outcome it has not yet seen fail. */
const turnsSentHere = new Set<string>();
const TURNS_SENT_HERE_LIMIT = 100;

/** This tab sent a turn on `threadId`: a failure it then shows is this turn's. */
export function noteTurnSent(threadId: string): void {
  turnsSentHere.delete(threadId);
  turnsSentHere.add(threadId);
  if (turnsSentHere.size > TURNS_SENT_HERE_LIMIT) {
    const oldest = turnsSentHere.values().next().value;
    if (oldest !== undefined) turnsSentHere.delete(oldest);
  }
}

/**
 * Whether the open thread has just started showing a failure:
 *
 * - the same thread as before, now with an error it did not have; or
 * - the first view of a thread this tab just sent a turn on, already showing
 *   an error: a new chat's first turn can fail before the thread's first
 *   snapshot arrives (`sentHere`).
 *
 * Opening a thread that already shows an old failure does not count; that
 * minute of replay is not the failure's.
 */
export function failureJustShown(
  previous: ShownFailure | null,
  next: ShownFailure,
  sentHere: boolean,
): boolean {
  if (next.threadId === null || next.error === null) return false;
  if (previous !== null && previous.threadId === next.threadId) return next.error !== previous.error;
  return sentHere;
}

/** Flush the replay when the open thread starts showing a failure. */
export function useFlushReplayOnShownFailure(threadId: string | null, error: string | null): void {
  const previous = useRef<ShownFailure | null>(null);
  useEffect(() => {
    const next = { threadId, error };
    const sentHere = threadId !== null && turnsSentHere.has(threadId);
    if (failureJustShown(previous.current, next, sentHere)) {
      // This turn's failure is accounted for; a later visit is not a new one.
      if (threadId !== null) turnsSentHere.delete(threadId);
      void flushReplayForFailure();
    }
    previous.current = next;
  }, [threadId, error]);
}
